import { useLocation, Location, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState, useContext } from 'react';
import { H1, TextLgRg, TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { Link } from '@/UI/Link/Link';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { WeavyTextLogo } from '@/UI/Icons/WeavyTextLogo';
import { Copyrights } from '@/components/Common/Copyrights';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import GoogleLogo from '@/UI/Icons/GoogleLogo';
import MicrosoftLogo from '@/UI/Icons/MicrosoftLogo';
import { FigmaLogo } from '@/UI/Icons/FigmaLogo';
import { AuthContext } from '@/contexts/AuthContext';
import { FlexColCenHorVer, Flex, FlexCol, FlexCenVer } from '@/UI/styles';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { DescopeSignInButtons } from '@/components/Common/Authentication/DescopeSignInButtons';
import { AUTH_PROVIDERS, DESCOPE_FLOW_IDS } from '@/consts/auth.consts';
import { useBanner, APP_BANNER_ID } from '@/hooks/useBanner';
import { FF_TERMS_OF_SERVICE } from '@/consts/featureFlags';
import { PageLoader } from '@/components/Common/PageLoader/PageLoader';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { IDP_INITIATED_REDIRECT_PATH } from './IdpInitiatedSignInPage';
import type { SignInOptions } from '@/contexts/AuthContext';

const useWrappedLocation = () => {
  const location = useLocation();
  return location as Location<{ from?: string }>;
};

const FirebaseSignInButtons = ({
  signIn,
  from,
  promotion,
  initiateRedirectSignIn,
}: {
  signIn: (options: SignInOptions) => Promise<void>;
  from: string;
  promotion: string | null;
  initiateRedirectSignIn: (provider: string, redirectPath: string) => Promise<void>;
}) => {
  const { t } = useTranslation();

  const googleSignIn = useCallback(
    () =>
      void signIn({
        route: from,
        providerType: 'google',
        promotion,
      }),
    [signIn, from, promotion],
  );

  const microsoftSignIn = useCallback(
    () =>
      void signIn({
        route: from,
        providerType: 'microsoft',
        promotion,
      }),
    [signIn, from, promotion],
  );

  const figmaSignIn = useCallback(() => {
    void initiateRedirectSignIn('figma', '/signin');
  }, [initiateRedirectSignIn]);

  return (
    <FlexCol sx={{ gap: 1, mb: 6 }}>
      <ButtonContained
        mode="outlined-light"
        size="large"
        onClick={googleSignIn}
        sx={{ gap: 1, width: '220px', justifyContent: 'flex-start' }}
      >
        <GoogleLogo width={20} height={20} title="Google logo" />
        <TextStdRg color={color.Black92}>{t(I18N_KEYS.SIGN_IN_PAGE.GOOGLE_SIGN_IN_BUTTON)}</TextStdRg>
      </ButtonContained>
      <ButtonContained
        mode="outlined-light"
        size="large"
        onClick={figmaSignIn}
        sx={{ gap: 1, width: '220px', justifyContent: 'flex-start' }}
      >
        <FigmaLogo />
        <TextStdRg color={color.Black92}>{t(I18N_KEYS.SIGN_IN_PAGE.FIGMA_SIGN_IN_BUTTON)}</TextStdRg>
      </ButtonContained>
      <ButtonContained
        mode="outlined-light"
        size="large"
        onClick={microsoftSignIn}
        sx={{ gap: 1, width: '220px', justifyContent: 'flex-start' }}
      >
        <MicrosoftLogo width={20} height={20} title="Microsoft logo" />
        <TextStdRg color={color.Black92}>{t(I18N_KEYS.SIGN_IN_PAGE.MICROSOFT_SIGN_IN_BUTTON)}</TextStdRg>
      </ButtonContained>
    </FlexCol>
  );
};

export function SignInPage() {
  const { t } = useTranslation();
  const { provider } = useAuthConfig();
  const isDescope = provider === AUTH_PROVIDERS.DESCOPE;
  const navigate = useNavigate();
  const location = useWrappedLocation();
  const [searchParams] = useSearchParams();
  const promotion = searchParams.get('promotion');
  const { offset } = useBanner(APP_BANNER_ID);
  const from: string = typeof location.state?.from === 'string' ? location.state.from : '/';
  const {
    currentUser,
    signIn,
    isSigningIn,
    onSignInSuccess,
    onSignInError,
    initiateRedirectSignIn,
    getStoredPromotion,
  } = useContext(AuthContext);
  const [authChecked, setAuthChecked] = useState(false);
  const [isSigninInProgress, setIsSigninInProgress] = useState(false);
  const { value: isTermsOfServiceEnabled } = useFeatureFlag(FF_TERMS_OF_SERVICE);

  // Handle Figma OAuth redirect
  useEffect(() => {
    const authorizationCode = searchParams.get('code');
    if (authorizationCode && !currentUser && !isSigningIn) {
      setIsSigninInProgress(true);
      // For Figma OAuth, use stored promotion and redirect path (post-login redirect) from sessionStorage
      const storedPromotion = getStoredPromotion();
      const storedRedirectPath = sessionStorage.getItem(IDP_INITIATED_REDIRECT_PATH);
      sessionStorage.removeItem(IDP_INITIATED_REDIRECT_PATH);

      const route = storedRedirectPath ?? from;

      void signIn({
        route,
        providerType: 'figma',
        authorizationCode,
        promotion: storedPromotion ?? promotion,
      });
    } else {
      setIsSigninInProgress(false);
    }
  }, [searchParams, currentUser, isSigningIn, signIn, from, promotion, getStoredPromotion, navigate]);

  useEffect(() => {
    if (currentUser && !isSigningIn) {
      // Get the redirect path from location state, default to '/' if not present
      const authorizationCode = searchParams.get('code');
      if (authorizationCode) {
        // navigation is handled directly in oauth flow
        return;
      }
      navigate(from);
    } else if (!currentUser) {
      setAuthChecked(true);
    }
  }, [currentUser, isSigningIn, navigate, from, searchParams]);

  const goHome = useCallback(() => (window.location.href = 'https://weave.figma.com'), []);

  if (!authChecked) {
    return null;
  }
  if (isSigninInProgress) {
    return <PageLoader />;
  }

  // TODO(figma-embed): defense-in-depth — detect Figma embed mode (e.g. via
  // `isFigmaEmbedRoute() || isInsideIframe()` from `@/utils/auth.utils`) and render an
  // inline access error instead of the signin UI. OAuth popups don't work inside the
  // Figma iframe and the CSP frame-ancestors blocks full-page navigation, so any caller
  // that misses the embed check and lands here should fail visibly rather than show a
  // broken sign-in screen.

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: `calc(100vh - ${offset}px)`,
        width: '100%',
        bgcolor: color.White100,
        position: 'relative',
        backgroundImage:
          'linear-gradient(0deg, #ffffff4d 34%, #c1cdd559 71%), url(https://cdn.prod.website-files.com/681b040781d5b5e278a69989/681ccdbeb607e939f7db68fa_BG%20NET%20Hero.avif)',
        backgroundPosition: '0 0, 50%',
        backgroundSize: 'auto, cover',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <FlexCol sx={{ flex: 1 }}>
        <Flex sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
          <WeavyTextLogo style={{ color: color.Black100, cursor: 'pointer' }} onClick={goHome} />
        </Flex>
        <Flex sx={{ display: { xs: 'flex', sm: 'none' }, position: 'absolute', top: 12, left: 12 }}>
          <WeavyLogoFilled
            title="Figma Weave Logo"
            width={36}
            height={36}
            style={{
              color: color.Black100,
              cursor: 'pointer',
            }}
            onClick={goHome}
          />
        </Flex>

        {/* Main Content */}
        <Flex
          data-testid="signin-page-content"
          sx={{
            gap: { xs: 0, sm: 6 },
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: { xs: '100%', sm: '362px' },
            height: { xs: '100%', sm: 'auto' },
            margin: { xs: '0', sm: 'auto' },
            borderRadius: { xs: 0, sm: 2 },
            bgcolor: color.White100,
            border: { xs: 'none', sm: `1px solid ${color.Black04}` },
          }}
        >
          <img
            src="/assets/weavy-sign-in-back.png"
            alt="Sign in cover"
            style={{ width: '100%', height: '270px', borderRadius: '8px 8px 0px 0px' }}
          />
          <FlexColCenHorVer sx={{}}>
            <H1 color={color.Black92} sx={{ fontSize: '28px', fontWeight: 400, mb: 1.5, mt: { xs: 6, sm: 0 } }}>
              {t(I18N_KEYS.SIGN_IN_PAGE.TITLE)}
            </H1>
            {!isTermsOfServiceEnabled && (
              <TextLgRg color={color.Black64_T} sx={{ mb: 4, fontSize: '14px' }}>
                {t(I18N_KEYS.SIGN_IN_PAGE.SUBTITLE)}
              </TextLgRg>
            )}
            {isTermsOfServiceEnabled && (
              <TextSmRg
                color={color.Black64_T}
                sx={{ mb: 3, textAlign: 'center', maxWidth: '234px', fontSize: '10px' }}
              >
                {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_TEXT)}{' '}
                <Link
                  href={EXTERNAL_LINKS.termsOfService}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: color.Yambo_Blue, textDecoration: 'underline' }}
                >
                  {t(I18N_KEYS.SIGN_IN_PAGE.TERMS_OF_SERVICE_LINK)}
                </Link>{' '}
                and{' '}
                <Link
                  href={EXTERNAL_LINKS.privacyPolicy}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: color.Yambo_Blue, textDecoration: 'underline' }}
                >
                  {t(I18N_KEYS.SIGN_IN_PAGE.PRIVACY_POLICY_LINK)}
                </Link>
                .
              </TextSmRg>
            )}
            {isDescope ? (
              <Flex sx={{ width: '234px', marginBottom: '48px' }}>
                <DescopeSignInButtons
                  flowId={DESCOPE_FLOW_IDS.MAIN_SIGN_IN_SCREEN}
                  onSignInSuccess={onSignInSuccess}
                  onSignInError={onSignInError}
                  promotion={promotion}
                  from={from}
                />
              </Flex>
            ) : (
              <FirebaseSignInButtons
                signIn={signIn}
                from={from}
                promotion={promotion}
                initiateRedirectSignIn={initiateRedirectSignIn}
              />
            )}
          </FlexColCenHorVer>
        </Flex>
      </FlexCol>
      <FlexCenVer
        sx={{
          py: 3,
          px: 3,
          justifyContent: { xs: 'center', sm: 'flex-end' },
        }}
      >
        <Copyrights textColor={color.Black84_T} />
      </FlexCenVer>
    </Flex>
  );
}
